import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Sparkles, Wand2, ArrowRight, Info, ChevronDown, ChevronUp, Check, Edit2, Loader, ArrowLeft, Upload, Image as ImageIcon, X, Trash2 } from 'lucide-react';
import { PLATFORM_PRESETS } from '../contexts/EditorContext';
import { generateCarouselContent } from '../services/openaiService';
import { generateCarouselImagesAsync, pollTaskStatus } from '../services/fluxService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const CarouselGeneratorPage = () => {
  const navigate = useNavigate();
  
  // Wizard state
  const [currentPhase, setCurrentPhase] = useState(1); // 1 = Content Generation, 2 = Style Selection
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Progress tracking for async generation
  const [generationProgress, setGenerationProgress] = useState({
    status: '',
    progress: 0,
    message: '',
    taskId: null
  });
  
  // Error state
  const [generationError, setGenerationError] = useState(null);
  const [keywordError, setKeywordError] = useState(null);
  
  // Phase 1: Content Generation
  const [contentFormData, setContentFormData] = useState({
    topic: '',
    keywords: '',
    slideCount: 5
  });
  
  // Generated content from OpenAI
  const [generatedSlides, setGeneratedSlides] = useState([]);
  
  // Phase 2: Style Selection
  const [styleFormData, setStyleFormData] = useState({
    platform: 'linkedin-4-5'
  });

  // Background type selection: 'colors' or 'styles'
  const [backgroundType, setBackgroundType] = useState('styles');
  
  // Solid colors & gradients state
  const [colorSettings, setColorSettings] = useState({
    type: 'solid', // 'solid', 'gradient', 'pattern'
    solidColor: '#8b5cf6', // Default purple
    gradientType: 'linear', // 'linear', 'radial'
    gradientDirection: 'to bottom right', // CSS gradient direction
    gradientColors: ['#8b5cf6', '#ec4899'], // Array of colors
    patternType: 'stripes', // 'stripes', 'waves', 'diagonal', 'dots', 'grid'
    patternColor1: '#8b5cf6',
    patternColor2: '#ec4899'
  });

  const imageStyleCategories = [
    {
      id: 'photographic',
      name: '📷 Photographic Styles',
      description: 'Real-life camera-based images',
      icon: '📸',
      styles: [
        'Ultra-realistic', 'Hyper-realistic', 'Natural daylight photography',
        'DSLR camera style', 'Mirrorless camera style', 'Macro photography',
        'Close-up photography', 'Studio photography', 'Cinematic photography',
        'Lifestyle photography', 'Product photography', 'Food photography', 'Portrait photography'
      ]
    },
    {
      id: 'artistic',
      name: '🎨 Artistic / Illustration',
      description: 'Drawn, painted, or illustrated styles',
      icon: '🖌️',
      styles: [
        'Vector art', 'Flat illustration', 'Line art', 'Watercolor painting',
        'Oil painting', 'Digital painting', 'Sketch', 'Pencil drawing',
        'Ink illustration', 'Pop art', 'Anime style', 'Manga style', 'Comic style'
      ]
    },
    {
      id: '3d-cgi',
      name: '🎬 3D & CGI',
      description: 'Computer-generated imagery',
      icon: '💎',
      styles: ['3D render', 'Pixar style', 'Claymation', 'Low-poly art', 'High-detail CGI', 'Isometric 3D']
    },
    {
      id: 'graphic-design',
      name: '✨ Graphic Design',
      description: 'Perfect for social media and marketing',
      icon: '📱',
      styles: [
        'Minimalist design', 'Modern corporate design', 'Typography-focused',
        'Collage style', 'Gradient background', 'Magazine cover style',
        'Infographic style', 'Flat + Photo hybrid'
      ]
    },
    {
      id: 'cinematic',
      name: '🎥 Cinematic / Dramatic',
      description: 'Storytelling and emotional visuals',
      icon: '🎭',
      styles: [
        'Film still', 'Cinematic lighting', 'Moody aesthetic', 'Dark aesthetic',
        'Vintage film', 'Fantasy realism', 'Noir style'
      ]
    },
    {
      id: 'surreal',
      name: '🌌 Surreal / Creative AI',
      description: 'Unique and imaginative styles',
      icon: '✨',
      styles: [
        'Dreamlike', 'Abstract', 'Surrealism', 'Dali-inspired',
        'Glitch art', 'Neon cyberpunk', 'Futuristic sci-fi'
      ]
    }
  ];

  const [selectedStyles, setSelectedStyles] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);

  // Logo upload state
  const [logos, setLogos] = useState([]);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPosition, setLogoPosition] = useState('bottom-right');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isLoadingLogos, setIsLoadingLogos] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState(null);
  const [isPaidUser, setIsPaidUser] = useState(false);

  // Predefined color palettes
  const colorPalettes = [
    { name: 'Purple Dream', colors: ['#8b5cf6', '#ec4899'] },
    { name: 'Ocean Blue', colors: ['#0ea5e9', '#06b6d4'] },
    { name: 'Sunset', colors: ['#f59e0b', '#ef4444'] },
    { name: 'Forest', colors: ['#10b981', '#059669'] },
    { name: 'Royal', colors: ['#6366f1', '#8b5cf6'] },
    { name: 'Fire', colors: ['#dc2626', '#f97316'] },
    { name: 'Mint', colors: ['#34d399', '#10b981'] },
    { name: 'Rose Gold', colors: ['#fbbf24', '#f472b6'] },
    { name: 'Night Sky', colors: ['#1e293b', '#475569'] },
    { name: 'Lavender', colors: ['#c084fc', '#e879f9'] },
    { name: 'Coral', colors: ['#fb7185', '#fda4af'] },
    { name: 'Teal', colors: ['#14b8a6', '#06b6d4'] }
  ];

  // Gradient directions
  const gradientDirections = [
    { value: 'to right', label: 'Right →', icon: '→' },
    { value: 'to bottom right', label: 'Bottom Right ↘', icon: '↘' },
    { value: 'to bottom', label: 'Bottom ↓', icon: '↓' },
    { value: 'to bottom left', label: 'Bottom Left ↙', icon: '↙' },
    { value: 'to left', label: 'Left ←', icon: '←' },
    { value: 'to top left', label: 'Top Left ↖', icon: '↖' },
    { value: 'to top', label: 'Top ↑', icon: '↑' },
    { value: 'to top right', label: 'Top Right ↗', icon: '↗' }
  ];

  // Pattern types
  const patternTypes = [
    { value: 'stripes', label: 'Stripes', icon: '▓' },
    { value: 'diagonal', label: 'Diagonal', icon: '⧅' },
    { value: 'checkerboard', label: 'Checkerboard', icon: '▦' },
    { value: 'dots', label: 'Dots', icon: '⚬' },
    { value: 'grid', label: 'Grid', icon: '▦' },
    { value: 'vertical', label: 'Vertical', icon: '║' },
    { value: 'horizontal', label: 'Horizontal', icon: '═' }
  ];

  // Phase 1 handlers
  const handleContentInputChange = (field, value) => {
    if (field === 'keywords') {
      // Validate keywords: comma-separated, max 5 keywords
      const keywords = value.split(',').map(k => k.trim()).filter(k => k.length > 0);
      
      if (keywords.length > 5) {
        setKeywordError('Maximum 5 keywords allowed. Please use comma-separated values.');
        return; // Don't update if more than 5 keywords
      } else {
        setKeywordError(null);
      }
    }
    
    setContentFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateContent = async () => {
    setIsGenerating(true);
    
    try {
      console.log('🚀 Generating content with OpenAI via Django backend...', contentFormData);
      
      // Call Django backend API
      const response = await generateCarouselContent(
        contentFormData.topic,
        contentFormData.keywords,
        contentFormData.slideCount
      );
      
      console.log('✅ OpenAI response received:', response);
      
      // Extract slides from response
      if (response.success && response.slides) {
        setGeneratedSlides(response.slides);
        setCurrentPhase(2); // Move to phase 2
      } else {
        throw new Error('Invalid response format from server');
      }
      
    } catch (error) {
      console.error('❌ Error generating content:', error);
      alert(error.message || 'Failed to generate content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Phase 2 handlers
  const handleStyleInputChange = (field, value) => {
    setStyleFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleStyle = (style) => {
    setSelectedStyles(prev => 
      prev.includes(style)
        ? prev.filter(s => s !== style)
        : [...prev, style]
    );
  };

  const handleEditSlide = (slideIndex, field, value) => {
    setGeneratedSlides(prev => prev.map((slide, idx) => 
      idx === slideIndex ? { ...slide, [field]: value } : slide
    ));
  };

  const handleBackToPhase1 = () => {
    setCurrentPhase(1);
  };

  // Fetch user subscription status
  useEffect(() => {
    const fetchUserSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setIsPaidUser(false);
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/ai/credits/balance/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data.success && response.data.balance) {
          // User is paid if they have an active package
          const hasActivePackage = response.data.balance.has_active_package || false;
          setIsPaidUser(hasActivePackage);
          console.log('User subscription status:', hasActivePackage ? 'Paid' : 'Free');
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        setIsPaidUser(false);
      }
    };

    fetchUserSubscriptionStatus();
  }, []);

  // Logo management functions
  useEffect(() => {
    // Fetch logos on component mount and when phase changes to 2
    fetchLogos();
  }, [currentPhase]);

  const fetchLogos = async () => {
    console.log('🔍 Fetching logos...');
    setIsLoadingLogos(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('❌ No access token found');
        setLogos([]);
        setIsLoadingLogos(false);
        return;
      }

      console.log('📡 Making API request to:', `${API_BASE_URL}/auth/logos/`);
      const response = await axios.get(`${API_BASE_URL}/auth/logos/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Logos API response:', response.data);
      
      // Handle both array and paginated response formats
      let logosData = [];
      if (Array.isArray(response.data)) {
        logosData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Paginated response
        logosData = response.data.results;
      } else if (response.data && typeof response.data === 'object') {
        console.warn('⚠️ Unexpected response format:', response.data);
        // Try to extract array from common response formats
        logosData = response.data.data || response.data.logos || [];
      }
      
      console.log(`📦 Setting ${logosData.length} logos to state`);
      setLogos(logosData);
    } catch (error) {
      console.error('❌ Error fetching logos:', error);
      console.error('Error details:', error.response?.data);
      setLogos([]); // Set to empty array on error
    } finally {
      setIsLoadingLogos(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setLogoUploadError('File size must be less than 5MB');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoUploadError('Only PNG, JPG, JPEG, WEBP, and SVG files are allowed');
      return;
    }

    setIsUploadingLogo(true);
    setLogoUploadError(null);

    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setLogoUploadError('Please log in to upload logos');
        setIsUploadingLogo(false);
        return;
      }

      const formData = new FormData();
      formData.append('logo_file', file);
      formData.append('name', file.name.split('.')[0]);

      const response = await axios.post(`${API_BASE_URL}/auth/logos/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - axios will set it automatically with boundary
        }
      });

      setLogos(prev => {
        const currentLogos = Array.isArray(prev) ? prev : [];
        return [...currentLogos, response.data];
      });
      setSelectedLogo(response.data.id);
      event.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Error uploading logo:', error);
      console.error('Error response:', error.response);
      
      // Extract detailed error message
      let errorMessage = 'Failed to upload logo';
      
      if (error.response?.data) {
        // Handle different error response formats
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.logo_file) {
          // Handle field-specific errors
          errorMessage = Array.isArray(error.response.data.logo_file) 
            ? error.response.data.logo_file[0] 
            : error.response.data.logo_file;
        } else if (error.response.data.name) {
          errorMessage = Array.isArray(error.response.data.name) 
            ? error.response.data.name[0] 
            : error.response.data.name;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setLogoUploadError(errorMessage);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async (logoId) => {
    if (!window.confirm('Are you sure you want to delete this logo?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_BASE_URL}/auth/logos/${logoId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setLogos(prev => {
        const currentLogos = Array.isArray(prev) ? prev : [];
        return currentLogos.filter(logo => logo.id !== logoId);
      });
      if (selectedLogo === logoId) {
        setSelectedLogo(null);
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
      alert('Failed to delete logo');
    }
  };

  const handleFinalGenerate = async () => {
    setIsGenerating(true);
    setGenerationError(null);
    setGenerationProgress({
      status: 'STARTING',
      progress: 0,
      message: 'Initializing image generation...',
      taskId: null
    });
    
    try {
      // Log the complete configuration for backend integration
      console.log('🚀 Starting carousel generation with configuration:', {
        backgroundType: backgroundType,
        slides: generatedSlides.length,
        platform: styleFormData.platform,
        logoId: selectedLogo,
        logoPosition: logoPosition,
        ...(backgroundType === 'colors' ? {
          colorSettings: {
            type: colorSettings.type,
            ...(colorSettings.type === 'solid' && {
              solidColor: colorSettings.solidColor
            }),
            ...(colorSettings.type === 'gradient' && {
              gradientType: colorSettings.gradientType,
              gradientDirection: colorSettings.gradientDirection,
              gradientColors: colorSettings.gradientColors
            }),
            ...(colorSettings.type === 'pattern' && {
              patternType: colorSettings.patternType,
              patternColor1: colorSettings.patternColor1,
              patternColor2: colorSettings.patternColor2
            })
          }
        } : {
          imageStyles: selectedStyles
        })
      });
      
      console.log('🚀 Starting async image generation...', {
        slides: generatedSlides.length,
        platform: styleFormData.platform,
        styles: selectedStyles,
        logoId: selectedLogo,
        logoPosition: logoPosition
      });
      
      // Step 1: Start async generation (returns immediately with task_id)
      const taskResponse = await generateCarouselImagesAsync(
        generatedSlides,
        styleFormData.platform,
        backgroundType === 'styles' ? selectedStyles : [],
        contentFormData.topic,
        contentFormData.keywords,
        backgroundType,
        backgroundType === 'colors' ? colorSettings : null,
        selectedLogo, // Pass logo ID
        logoPosition // Pass logo position
      );
      
      const taskId = taskResponse.task_id;
      console.log('✅ Task queued with ID:', taskId);
      
      setGenerationProgress({
        status: 'QUEUED',
        progress: 5,
        message: 'Task queued. Waiting for processing...',
        taskId: taskId
      });
      
      // Step 2: Poll for task completion with progress updates
      const result = await pollTaskStatus(taskId, (statusUpdate) => {
        // Update progress UI in real-time
        setGenerationProgress({
          status: statusUpdate.status,
          progress: statusUpdate.progress || 0,
          message: statusUpdate.message || statusUpdate.status_message || 'Processing...',
          taskId: taskId
        });
        
        console.log(`📊 Progress: ${statusUpdate.progress}% - ${statusUpdate.message || statusUpdate.status}`);
      });
      
      console.log('✅ Generation completed:', result);
      
      // Step 3: Navigate to carousel detail page or preview
      if (result.success && result.carousel_id) {
        // Navigate to the generated carousel
        navigate(`/carousel/${result.carousel_id}`);
      } else {
        throw new Error('Generation completed but no carousel was created');
      }
      
    } catch (error) {
      console.error('❌ Error generating images:', error);
      setGenerationError(error.message || 'Failed to generate images. Please try again.');
      
      // Show error in progress state
      setGenerationProgress(prev => ({
        ...prev,
        status: 'FAILURE',
        message: error.message || 'Generation failed'
      }));
    } finally {
      // Keep isGenerating true if showing error (for retry button)
      if (!generationError) {
        setIsGenerating(false);
      }
    }
  };
  
  // Retry generation after error
  const handleRetry = () => {
    setGenerationError(null);
    setIsGenerating(false);
    setGenerationProgress({
      status: '',
      progress: 0,
      message: '',
      taskId: null
    });
  };

  const isPhase1Valid = contentFormData.topic.trim().length > 0;
  const isPhase2Valid = backgroundType === 'colors' ? true : selectedStyles.length > 0;

  return (
    <>
      <Helmet>
        <title>Create New Carousel - AI Generator | AI Carousel</title>
        <meta 
          name="description" 
          content="Generate engaging carousel content with AI in minutes. Choose your platform, enter your topic, and let AI create stunning slides." 
        />
        <meta 
          name="keywords" 
          content="create carousel, AI generator, content generation, new carousel, AI content, social media creator" 
        />
        <meta property="og:title" content="Create New Carousel with AI" />
        <meta property="og:description" content="Generate professional carousel posts in minutes" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Wand2 className="h-8 w-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  AI Carousel Generator
                </h1>
                <p className="text-sm text-gray-600">
                  {currentPhase === 1 ? 'Step 1: Generate Content' : 'Step 2: Choose Style & Platform'}
                </p>
              </div>
            </div>

            {/* Phase Indicator */}
            <div className="flex items-center space-x-2">
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                currentPhase === 1 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
              }`}>
                <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <span className="text-sm font-medium hidden sm:block">Content</span>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                currentPhase === 2 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
              }`}>
                <div className={`w-6 h-6 rounded-full ${
                  currentPhase === 2 ? 'bg-purple-600' : 'bg-gray-300'
                } text-white flex items-center justify-center text-sm font-bold`}>
                  2
                </div>
                <span className="text-sm font-medium hidden sm:block">Style</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* PHASE 1: Content Generation */}
        {currentPhase === 1 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                ✨ What's Your Carousel About?
              </h2>
              <p className="text-gray-600">
                Tell us your topic and we'll generate compelling content for each slide.
              </p>
            </div>

            {/* Topic Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Topic or Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={contentFormData.topic}
                onChange={(e) => handleContentInputChange('topic', e.target.value)}
                placeholder="e.g., 5 tips for better lung health, 10 productivity hacks for remote workers"
                className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
              />
              <p className="mt-2 text-sm text-gray-500">
                💡 Be specific! Better topics lead to better content.
              </p>
            </div>

            {/* Keywords Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Keywords (Optional)
              </label>
              <input
                type="text"
                value={contentFormData.keywords}
                onChange={(e) => handleContentInputChange('keywords', e.target.value)}
                placeholder="e.g., health, wellness, breathing, fitness, tips"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-colors ${
                  keywordError 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                    : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
                }`}
              />
              {keywordError ? (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <X className="h-4 w-4 mr-1" />
                  {keywordError}
                </p>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  Separate keywords with commas (max 5) to guide AI content generation
                </p>
              )}
              {contentFormData.keywords && !keywordError && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {contentFormData.keywords.split(',').map((keyword, idx) => {
                    const trimmed = keyword.trim();
                    if (!trimmed) return null;
                    return (
                      <span key={idx} className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                        {trimmed}
                      </span>
                    );
                  })}
                  <span className="text-xs text-gray-500 self-center">
                    ({contentFormData.keywords.split(',').filter(k => k.trim()).length}/5 keywords)
                  </span>
                </div>
              )}
            </div>

            {/* Slide Count */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Number of Slides
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="3"
                  max="10"
                  value={contentFormData.slideCount}
                  onChange={(e) => handleContentInputChange('slideCount', parseInt(e.target.value))}
                  className="flex-1 h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
                <span className="text-3xl font-bold text-purple-600 w-16 text-center">
                  {contentFormData.slideCount}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                💡 Recommended: 5-7 slides for best engagement
              </p>
            </div>

            {/* Info Box */}
            <div className="mb-8 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-2">✨ What Happens Next:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-800">
                    <li>We'll generate compelling <strong>headers</strong> and <strong>descriptions</strong> for each slide</li>
                    <li>You can <strong>review and edit</strong> all generated content</li>
                    <li>Customize the text to match your voice and style</li>
                    <li>Choose the style of background images you want</li>
                    <li>Download your carousel ready for social media</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-6 border-t">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 text-gray-600 hover:text-gray-900 font-medium transition-colors flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Dashboard</span>
              </button>
              
              <button
                onClick={handleGenerateContent}
                disabled={!isPhase1Valid || isGenerating}
                className={`px-8 py-4 rounded-xl font-semibold flex items-center space-x-2 transition-all ${
                  isPhase1Valid && !isGenerating
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isGenerating ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Generating Content...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    <span>Generate Content with AI</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* PHASE 2: Style Selection & Generated Content Review */}
        {currentPhase === 2 && (
          <div className="space-y-6">
            {/* Generated Content Review */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    📝 Generated Content
                  </h2>
                  <p className="text-gray-600">
                    Review and edit the AI-generated content. Click any field to edit.
                  </p>
                </div>
                <button
                  onClick={handleBackToPhase1}
                  className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Edit Topic</span>
                </button>
              </div>

              {/* Slides Preview */}
              <div className="space-y-4">
                {generatedSlides.map((slide, index) => (
                  <div key={index} className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 transition-colors">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 text-purple-600 font-bold flex items-center justify-center">
                        {slide.slideNumber}
                      </div>
                      <div className="flex-1 space-y-3">
                        {/* Header */}
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Header</label>
                          <input
                            type="text"
                            value={slide.header}
                            onChange={(e) => handleEditSlide(index, 'header', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-semibold text-lg mt-1"
                          />
                        </div>

                        {/* Description */}
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</label>
                          <textarea
                            value={slide.description}
                            onChange={(e) => handleEditSlide(index, 'description', e.target.value)}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mt-1"
                          />
                        </div>

                      </div>
                      <Edit2 className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Background Type Selection */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  🎨 Choose Background Type
                </h2>
                <p className="text-gray-600">
                  Select between solid colors/gradients or AI-generated image styles.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setBackgroundType('styles')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    backgroundType === 'styles'
                      ? 'border-purple-500 bg-purple-50 shadow-lg'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-4xl mb-3">📸</div>
                  <div className="text-lg font-bold text-gray-900 mb-1">
                    AI Image Styles
                  </div>
                  <div className="text-sm text-gray-600">
                    Generate images with AI (Photographic, Artistic, 3D, etc.)
                  </div>
                </button>

                <button
                  onClick={() => setBackgroundType('colors')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    backgroundType === 'colors'
                      ? 'border-purple-500 bg-purple-50 shadow-lg'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-4xl mb-3">🎨</div>
                  <div className="text-lg font-bold text-gray-900 mb-1">
                    Solid Colors & Gradients
                  </div>
                  <div className="text-sm text-gray-600">
                    Use solid colors, gradients, or patterns
                  </div>
                </button>
              </div>
            </div>

            {/* Solid Colors & Gradients Section */}
            {backgroundType === 'colors' && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    🎨 Customize Colors & Patterns
                  </h2>
                  <p className="text-gray-600">
                    Create beautiful backgrounds with colors, gradients, and patterns.
                  </p>
                </div>

                {/* Type Selection: Solid, Gradient, Pattern */}
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Background Type
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setColorSettings(prev => ({ ...prev, type: 'solid' }))}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        colorSettings.type === 'solid'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">⬛</div>
                      <div className="text-sm font-medium">Solid Color</div>
                    </button>
                    <button
                      onClick={() => setColorSettings(prev => ({ ...prev, type: 'gradient' }))}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        colorSettings.type === 'gradient'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">🌈</div>
                      <div className="text-sm font-medium">Gradient</div>
                    </button>
                    <button
                      onClick={() => setColorSettings(prev => ({ ...prev, type: 'pattern' }))}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        colorSettings.type === 'pattern'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">▓</div>
                      <div className="text-sm font-medium">Pattern</div>
                    </button>
                  </div>
                </div>

                {/* Solid Color Options */}
                {colorSettings.type === 'solid' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Choose Color
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={colorSettings.solidColor}
                          onChange={(e) => setColorSettings(prev => ({ ...prev, solidColor: e.target.value }))}
                          className="h-16 w-24 rounded-lg border-2 border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={colorSettings.solidColor}
                          onChange={(e) => setColorSettings(prev => ({ ...prev, solidColor: e.target.value }))}
                          className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-mono text-sm"
                          placeholder="#8b5cf6"
                        />
                      </div>
                    </div>

                    {/* Quick Color Palettes */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Quick Palettes
                      </label>
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                        {colorPalettes.map((palette) => (
                          <button
                            key={palette.name}
                            onClick={() => setColorSettings(prev => ({ ...prev, solidColor: palette.colors[0] }))}
                            className="group relative"
                            title={palette.name}
                          >
                            <div
                              className="h-12 rounded-lg border-2 border-gray-300 hover:border-purple-500 transition-all hover:scale-105"
                              style={{ backgroundColor: palette.colors[0] }}
                            />
                            <div className="text-xs text-gray-600 mt-1 text-center truncate">
                              {palette.name}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preview */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Preview
                      </label>
                      <div
                        className="h-32 rounded-xl border-2 border-gray-300"
                        style={{ backgroundColor: colorSettings.solidColor }}
                      />
                    </div>
                  </div>
                )}

                {/* Gradient Options */}
                {colorSettings.type === 'gradient' && (
                  <div className="space-y-6">
                    {/* Gradient Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Gradient Type
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setColorSettings(prev => ({ ...prev, gradientType: 'linear' }))}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            colorSettings.gradientType === 'linear'
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-sm font-medium">Linear Gradient</div>
                        </button>
                        <button
                          onClick={() => setColorSettings(prev => ({ ...prev, gradientType: 'radial' }))}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            colorSettings.gradientType === 'radial'
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-sm font-medium">Radial Gradient</div>
                        </button>
                      </div>
                    </div>

                    {/* Gradient Colors */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Gradient Colors
                      </label>
                      <div className="space-y-3">
                        {colorSettings.gradientColors.map((color, index) => (
                          <div key={index} className="flex items-center space-x-4">
                            <span className="text-sm font-medium text-gray-600 w-16">
                              Color {index + 1}
                            </span>
                            <input
                              type="color"
                              value={color}
                              onChange={(e) => {
                                const newColors = [...colorSettings.gradientColors];
                                newColors[index] = e.target.value;
                                setColorSettings(prev => ({ ...prev, gradientColors: newColors }));
                              }}
                              className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={color}
                              onChange={(e) => {
                                const newColors = [...colorSettings.gradientColors];
                                newColors[index] = e.target.value;
                                setColorSettings(prev => ({ ...prev, gradientColors: newColors }));
                              }}
                              className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-mono text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => {
                          if (colorSettings.gradientColors.length < 5) {
                            setColorSettings(prev => ({
                              ...prev,
                              gradientColors: [...prev.gradientColors, '#6366f1']
                            }));
                          }
                        }}
                        disabled={colorSettings.gradientColors.length >= 5}
                        className="mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium disabled:text-gray-400"
                      >
                        + Add Color (max 5)
                      </button>
                    </div>

                    {/* Gradient Direction (Linear only) */}
                    {colorSettings.gradientType === 'linear' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Direction
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                          {gradientDirections.map((dir) => (
                            <button
                              key={dir.value}
                              onClick={() => setColorSettings(prev => ({ ...prev, gradientDirection: dir.value }))}
                              className={`p-3 rounded-lg border-2 transition-all ${
                                colorSettings.gradientDirection === dir.value
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="text-xl mb-1">{dir.icon}</div>
                              <div className="text-xs">{dir.label.split(' ')[0]}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quick Gradient Palettes */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Quick Gradient Palettes
                      </label>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                        {colorPalettes.map((palette) => (
                          <button
                            key={palette.name}
                            onClick={() => setColorSettings(prev => ({ ...prev, gradientColors: palette.colors }))}
                            className="group relative"
                            title={palette.name}
                          >
                            <div
                              className="h-12 rounded-lg border-2 border-gray-300 hover:border-purple-500 transition-all hover:scale-105"
                              style={{
                                background: `linear-gradient(to right, ${palette.colors.join(', ')})`
                              }}
                            />
                            <div className="text-xs text-gray-600 mt-1 text-center truncate">
                              {palette.name}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preview */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Preview
                      </label>
                      <div
                        className="h-32 rounded-xl border-2 border-gray-300"
                        style={{
                          background: colorSettings.gradientType === 'linear'
                            ? `linear-gradient(${colorSettings.gradientDirection}, ${colorSettings.gradientColors.join(', ')})`
                            : `radial-gradient(circle, ${colorSettings.gradientColors.join(', ')})`
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Pattern Options */}
                {colorSettings.type === 'pattern' && (
                  <div className="space-y-6">
                    {/* Pattern Type Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Pattern Style
                      </label>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                        {patternTypes.map((pattern) => (
                          <button
                            key={pattern.value}
                            onClick={() => setColorSettings(prev => ({ ...prev, patternType: pattern.value }))}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              colorSettings.patternType === pattern.value
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="text-2xl mb-1">{pattern.icon}</div>
                            <div className="text-xs font-medium">{pattern.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Pattern Colors */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Pattern Colors
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-4">
                          <span className="text-sm font-medium text-gray-600 w-24">
                            Primary
                          </span>
                          <input
                            type="color"
                            value={colorSettings.patternColor1}
                            onChange={(e) => setColorSettings(prev => ({ ...prev, patternColor1: e.target.value }))}
                            className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={colorSettings.patternColor1}
                            onChange={(e) => setColorSettings(prev => ({ ...prev, patternColor1: e.target.value }))}
                            className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-mono text-sm"
                          />
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-sm font-medium text-gray-600 w-24">
                            Secondary
                          </span>
                          <input
                            type="color"
                            value={colorSettings.patternColor2}
                            onChange={(e) => setColorSettings(prev => ({ ...prev, patternColor2: e.target.value }))}
                            className="h-12 w-20 rounded-lg border-2 border-gray-300 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={colorSettings.patternColor2}
                            onChange={(e) => setColorSettings(prev => ({ ...prev, patternColor2: e.target.value }))}
                            className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-mono text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Quick Pattern Palettes */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Quick Color Combinations
                      </label>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                        {colorPalettes.map((palette) => (
                          <button
                            key={palette.name}
                            onClick={() => setColorSettings(prev => ({
                              ...prev,
                              patternColor1: palette.colors[0],
                              patternColor2: palette.colors[1]
                            }))}
                            className="group relative"
                            title={palette.name}
                          >
                            <div className="h-12 rounded-lg border-2 border-gray-300 hover:border-purple-500 transition-all hover:scale-105 flex">
                              <div className="flex-1" style={{ backgroundColor: palette.colors[0] }} />
                              <div className="flex-1" style={{ backgroundColor: palette.colors[1] }} />
                            </div>
                            <div className="text-xs text-gray-600 mt-1 text-center truncate">
                              {palette.name}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preview */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Preview
                      </label>
                      <div 
                        className="h-32 rounded-xl border-2 border-gray-300 relative overflow-hidden"
                        style={{
                          background: colorSettings.patternType === 'stripes'
                            ? `repeating-linear-gradient(90deg, ${colorSettings.patternColor1} 0px, ${colorSettings.patternColor1} 20px, ${colorSettings.patternColor2} 20px, ${colorSettings.patternColor2} 40px)`
                            : colorSettings.patternType === 'vertical'
                            ? `repeating-linear-gradient(90deg, ${colorSettings.patternColor1} 0px, ${colorSettings.patternColor1} 10px, ${colorSettings.patternColor2} 10px, ${colorSettings.patternColor2} 20px)`
                            : colorSettings.patternType === 'horizontal'
                            ? `repeating-linear-gradient(0deg, ${colorSettings.patternColor1} 0px, ${colorSettings.patternColor1} 10px, ${colorSettings.patternColor2} 10px, ${colorSettings.patternColor2} 20px)`
                            : colorSettings.patternType === 'diagonal'
                            ? `repeating-linear-gradient(45deg, ${colorSettings.patternColor1} 0px, ${colorSettings.patternColor1} 20px, ${colorSettings.patternColor2} 20px, ${colorSettings.patternColor2} 40px)`
                            : colorSettings.patternType === 'dots'
                            ? `radial-gradient(circle, ${colorSettings.patternColor1} 2px, transparent 2px)`
                            : 'transparent',
                          backgroundSize: colorSettings.patternType === 'dots' ? '15px 15px' : 'auto',
                          backgroundPosition: '0 0',
                          backgroundColor: colorSettings.patternType === 'dots' ? colorSettings.patternColor2 : 'transparent'
                        }}
                      >
                        {colorSettings.patternType === 'checkerboard' && (
                          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <pattern id="checkerboard" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                                <rect width="20" height="20" fill={colorSettings.patternColor1}/>
                                <rect x="20" y="0" width="20" height="20" fill={colorSettings.patternColor2}/>
                                <rect x="0" y="20" width="20" height="20" fill={colorSettings.patternColor2}/>
                                <rect x="20" y="20" width="20" height="20" fill={colorSettings.patternColor1}/>
                              </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill="url(#checkerboard)"/>
                          </svg>
                        )}
                        {colorSettings.patternType === 'grid' && (
                          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <pattern id="grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                <rect width="20" height="20" fill={colorSettings.patternColor2}/>
                                <rect width="1" height="20" fill={colorSettings.patternColor1}/>
                                <rect width="20" height="1" fill={colorSettings.patternColor1}/>
                              </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill="url(#grid)"/>
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Platform Selection */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  📱 Choose Platform & Size
                </h2>
                <p className="text-gray-600">
                  Select the platform where you'll post your carousel.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(PLATFORM_PRESETS)
                  .filter(([key]) => key !== 'custom')
                  .map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => handleStyleInputChange('platform', key)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        styleFormData.platform === key
                          ? 'border-purple-500 bg-purple-50 shadow-md'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-sm font-semibold text-gray-900 mb-1">
                        {preset.name.replace(' (Recommended)', '')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {preset.width} × {preset.height}
                      </div>
                      <div className="text-xs text-gray-500">
                        {preset.aspectRatio || 'Custom'}
                      </div>
                      {preset.recommended && (
                        <div className="mt-2">
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            ⭐
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
              </div>
            </div>

            {/* Image Style Selection - Only show when backgroundType is 'styles' */}
            {backgroundType === 'styles' && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      🎨 Choose Image Styles
                    </h2>
                    <p className="text-gray-600">
                      Select image styles for AI generation. Mix and match for variety!
                    </p>
                  </div>
                  {selectedStyles.length > 0 && (
                    <span className="text-sm text-purple-600 font-semibold">
                      {selectedStyles.length} style{selectedStyles.length > 1 ? 's' : ''} selected
                    </span>
                  )}
                </div>

              {/* Accordion Style Categories */}
              <div className="space-y-3">
                {imageStyleCategories.map((category) => {
                  const isExpanded = expandedCategories.includes(category.id);
                  const categorySelectedCount = category.styles.filter(s => selectedStyles.includes(s)).length;
                  
                  return (
                    <div
                      key={category.id}
                      className="border-2 border-gray-200 rounded-xl overflow-hidden transition-all hover:border-purple-300"
                    >
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{category.icon}</span>
                          <div className="text-left">
                            <h3 className="font-semibold text-gray-900">
                              {category.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {category.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {categorySelectedCount > 0 && (
                            <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded">
                              {categorySelectedCount}
                            </span>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                      </button>

                      {/* Category Styles - Tags */}
                      {isExpanded && (
                        <div className="p-4 bg-white">
                          <div className="flex flex-wrap gap-2">
                            {category.styles.map((style) => {
                              const isSelected = selectedStyles.includes(style);
                              return (
                                <button
                                  key={style}
                                  onClick={() => toggleStyle(style)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-1 ${
                                    isSelected
                                      ? 'bg-purple-600 text-white shadow-md hover:bg-purple-700'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                                  }`}
                                >
                                  {isSelected && <Check className="h-3 w-3" />}
                                  <span>{style}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Selected Styles Summary */}
              {selectedStyles.length > 0 && (
                <div className="mt-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-xl">
                  <h4 className="text-sm font-semibold text-purple-900 mb-3">
                    ✨ Selected Styles ({selectedStyles.length}):
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedStyles.map((style) => (
                      <span
                        key={style}
                        className="inline-flex items-center space-x-1 bg-white px-3 py-1 rounded-full text-sm text-purple-700 border border-purple-300"
                      >
                        <span>{style}</span>
                        <button
                          onClick={() => toggleStyle(style)}
                          className="hover:text-purple-900 ml-1"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <button
                      onClick={() => setSelectedStyles([])}
                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
              </div>
            )}

            {/* Logo Upload Section - Hidden (users can now add logos via editor) */}
            {false && (
            <div className="bg-white rounded-2xl shadow-xl p-8 relative">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    🏷️ Add Your Logo (Optional) {!isPaidUser && <span className="text-sm font-normal text-purple-600 ml-2">👑 Premium</span>}
                  </h2>
                  <p className="text-gray-600">
                    Upload your logo to add branding to your carousel slides
                  </p>
                </div>
              </div>

              {/* Premium Feature Notice for Free Users */}
              {!isPaidUser && (
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">👑</div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-purple-900 mb-1">Premium Feature</h3>
                      <p className="text-sm text-purple-700 mb-3">
                        Logo branding is available for paid users only. Upgrade to add your logo to carousel slides and enhance your brand presence.
                      </p>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
                      >
                        Upgrade Now →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Area */}
              <div className="mb-6">
                <label className="block">
                  <div className={`border-2 border-dashed border-gray-300 rounded-xl p-6 transition-colors bg-gray-50 ${
                    isPaidUser ? 'hover:border-purple-400 cursor-pointer hover:bg-purple-50' : 'opacity-60 cursor-not-allowed'
                  }`}>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      disabled={isUploadingLogo || !isPaidUser}
                      className="hidden"
                    />
                    <div className="text-center">
                      {isUploadingLogo ? (
                        <div className="flex flex-col items-center">
                          <Loader className="h-8 w-8 text-purple-600 animate-spin mb-2" />
                          <p className="text-sm text-gray-600">Uploading logo...</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Click to upload logo
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, WEBP, SVG (max 5MB)
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </label>
                
                {logoUploadError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <X className="h-4 w-4 mr-1" />
                    {logoUploadError}
                  </p>
                )}
              </div>

              {/* Logo Position Selector */}
              <div className="mb-6">
                <label className="block mb-2">
                  <span className="text-sm font-semibold text-gray-700">Logo Position</span>
                  <p className="text-xs text-gray-500 mb-2">Choose where to place your logo on the carousel</p>
                </label>
                <select
                  value={logoPosition}
                  onChange={(e) => setLogoPosition(e.target.value)}
                  disabled={!isPaidUser}
                  className={`w-full px-4 py-3 border-2 border-gray-300 rounded-xl transition-all bg-white text-gray-900 font-medium ${
                    isPaidUser ? 'focus:border-purple-500 focus:ring-2 focus:ring-purple-200 cursor-pointer' : 'opacity-60 cursor-not-allowed'
                  }`}
                >
                  <option value="top-left">📍 Top Left</option>
                  <option value="top-center">📍 Top Center</option>
                  <option value="top-right">📍 Top Right</option>
                  <option value="bottom-left">📍 Bottom Left</option>
                  <option value="bottom-center">📍 Bottom Center</option>
                  <option value="bottom-right">📍 Bottom Right</option>
                </select>
              </div>

              {/* Uploaded Logos Grid */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Your Logos {logos.length > 0 && `(${logos.length})`}
                </h3>
                
                {/* Loading State */}
                {isLoadingLogos && (
                  <div className="text-center py-8">
                    <Loader className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Loading your logos...</p>
                  </div>
                )}

                {/* Logos Grid */}
                {!isLoadingLogos && logos.length > 0 && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {logos.map((logo) => (
                        <div
                          key={logo.id}
                          className={`relative group border-2 rounded-lg overflow-hidden transition-all ${
                            selectedLogo === logo.id
                              ? 'border-purple-500 ring-2 ring-purple-200'
                              : 'border-gray-200 hover:border-purple-300'
                          } ${
                            isPaidUser ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'
                          }`}
                          onClick={() => isPaidUser && setSelectedLogo(selectedLogo === logo.id ? null : logo.id)}
                        >
                          {/* Logo Image */}
                          <div className="aspect-square bg-gray-100 flex items-center justify-center p-2">
                            <img
                              src={logo.logo_url}
                              alt={logo.name}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          
                          {/* Logo Name */}
                          <div className="p-2 bg-white border-t border-gray-200">
                            <p className="text-xs text-gray-700 truncate font-medium">
                              {logo.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {logo.file_format?.toUpperCase()}
                            </p>
                          </div>

                          {/* Selected Indicator */}
                          {selectedLogo === logo.id && (
                            <div className="absolute top-2 right-2 bg-purple-600 text-white rounded-full p-1">
                              <Check className="h-3 w-3" />
                            </div>
                          )}

                          {/* Delete Button */}
                          {isPaidUser && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLogo(logo.id);
                              }}
                              className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {selectedLogo && (
                      <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Check className="h-4 w-4 text-purple-600" />
                          <span className="text-sm text-purple-900 font-medium">
                            Logo selected - will be added to your carousel
                          </span>
                        </div>
                        <button
                          onClick={() => setSelectedLogo(null)}
                          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Empty State */}
                {!isLoadingLogos && logos.length === 0 && (
                  <div className="text-center py-6">
                    <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No logos uploaded yet</p>
                    <p className="text-xs text-gray-400 mt-1">Upload your first logo to get started</p>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Final Action Buttons */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBackToPhase1}
                  className="px-6 py-3 text-gray-600 hover:text-gray-900 font-medium transition-colors flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Content</span>
                </button>
                
                <button
                  onClick={handleFinalGenerate}
                  disabled={!isPhase2Valid || isGenerating}
                  className={`px-8 py-4 rounded-xl font-semibold flex items-center space-x-2 transition-all ${
                    isPhase2Valid && !isGenerating
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Generating Images...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5" />
                      <span>Generate Images & Create Carousel</span>
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Progress Modal */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center">
              {/* Spinner or Success Icon */}
              {generationProgress.status === 'FAILURE' ? (
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">❌</span>
                </div>
              ) : generationProgress.progress === 100 ? (
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">✅</span>
                </div>
              ) : (
                <div className="flex justify-center mb-4">
                  <Loader className="h-16 w-16 text-purple-600 animate-spin" />
                </div>
              )}
              
              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {generationError ? 'Generation Failed' : 
                 generationProgress.progress === 100 ? 'Almost Done!' : 
                 'Generating Your Carousel'}
              </h3>
              
              {/* Progress Bar */}
              {!generationError && (
                <div className="w-full bg-gray-200 rounded-full h-4 mb-4 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-purple-600 to-pink-600 h-4 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${generationProgress.progress}%` }}
                  />
                </div>
              )}
              
              {/* Progress Text */}
              <p className="text-lg font-semibold text-gray-700 mb-2">
                {generationError ? '' : `${generationProgress.progress}% Complete`}
              </p>
              
              {/* Status Message */}
              <p className="text-sm text-gray-600 mb-6">
                {generationError || generationProgress.message || 'Processing...'}
              </p>
              
              {/* Progress Steps */}
              {!generationError && (
                <div className="space-y-3 mb-6 text-left">
                  <div className={`flex items-center space-x-3 ${generationProgress.progress >= 5 ? 'text-green-600' : 'text-gray-400'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress.progress >= 5 ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {generationProgress.progress >= 5 ? '✓' : '○'}
                    </div>
                    <span className="text-sm font-medium">Task queued</span>
                  </div>
                  
                  <div className={`flex items-center space-x-3 ${generationProgress.progress >= 20 ? 'text-green-600' : 'text-gray-400'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress.progress >= 20 ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {generationProgress.progress >= 20 ? '✓' : '○'}
                    </div>
                    <span className="text-sm font-medium">Generating images</span>
                  </div>
                  
                  <div className={`flex items-center space-x-3 ${generationProgress.progress >= 90 ? 'text-green-600' : 'text-gray-400'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress.progress >= 90 ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {generationProgress.progress >= 90 ? '✓' : '○'}
                    </div>
                    <span className="text-sm font-medium">Finalizing carousel</span>
                  </div>
                  
                  <div className={`flex items-center space-x-3 ${generationProgress.progress === 100 ? 'text-green-600' : 'text-gray-400'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress.progress === 100 ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {generationProgress.progress === 100 ? '✓' : '○'}
                    </div>
                    <span className="text-sm font-medium">Complete!</span>
                  </div>
                </div>
              )}
              
              {/* Info Box */}
              {!generationError && generationProgress.progress < 100 && (
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-2">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800 text-left">
                      This may take a few minutes depending on the number of slides.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Error Message and Retry Button */}
              {generationError && (
                <div className="space-y-4">
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-red-800">
                      {generationError}
                    </p>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={handleRetry}
                      className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={() => {
                        setIsGenerating(false);
                        setGenerationError(null);
                      }}
                      className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              {/* Task ID for debugging */}
              {generationProgress.taskId && (
                <p className="text-xs text-gray-400 mt-4">
                  Task ID: {generationProgress.taskId}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default CarouselGeneratorPage;
